import styles from "./AppBar.module.css";
import { useState, useEffect, useRef, useMemo } from "react";
import logoImage from "../../assets/icons/logo.png";
import darkLogo from "../../assets/icons/dark-mode-logo.png";
import avatarImage from "../../assets/icons/avatar.png";
import ThemeSwitch from "../ThemeSwitch/ThemeSwitch";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import useDashboardStore from "../../store/dashboardStore";
import chatStore from "../../store/chatStore";
import { ENDPOINTS } from "../../utils/endpoints";
import { FiSearch } from "react-icons/fi";
import BadgeCount from "../ChatPanel/BadgeCount";
import NotificationCenter from "../NotificationCenter/NotificationCenter";

const AppBar = ({ user }) => {
  const { setIsMyProfileScreen, setIsMyMembershipScreen } = useDashboardStore();
  const { logout, userMeta } = useAuthStore();
  const {
    allChannels,
    communities,
    userJoinedChannels,
    setSelectedChannel,
    setIsChatScreen,
    setCommunityIndex,
  } = useDashboardStore();
  const { unreadChatCounts } = chatStore();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    document.documentElement.setAttribute("data-theme", next);
    localStorage.setItem("theme", next);
  };

  // Close profile menu on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(false);
      }
    }
    if (openMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenu]);

  // Focus search input when sheet opens
  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearchQuery("");
    }
  }, [searchOpen]);

  // Close search on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && searchOpen) setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  // Filtered channels — client-side, searches name OR community name
  const filteredChannels = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return allChannels.filter(
      (ch) =>
        ch.name?.toLowerCase().includes(q) ||
        ch.community?.toLowerCase().includes(q)
    );
  }, [searchQuery, allChannels]);

  const handleChannelSelect = (channel) => {
    const communityIdx = communities.findIndex(
      (c) => c.name === channel.community
    );
    if (communityIdx !== -1) setCommunityIndex(communityIdx);
    setSelectedChannel(channel);
    setIsChatScreen();
    setSearchOpen(false);
  };

  const profilePicture = userMeta?.profile_picture_url || avatarImage;

  return (
    <>
      <div className={styles.appBar}>
        <img
          src={theme === "light" ? logoImage : darkLogo}
          className={styles.logo}
          alt="logo"
        />

        <div className={styles.actions}>
          {/* Global search icon */}
          <button
            className={styles.searchBtn}
            onClick={() => setSearchOpen(true)}
            title="Search channels"
          >
            <FiSearch />
          </button>

          <NotificationCenter />

          <ThemeSwitch theme={theme} toggleTheme={toggleTheme} />

          <div className={styles.profileSection}>
            <div
              onClick={() => setOpenMenu(!openMenu)}
              className={styles.profileInfo}
            >
              <img
                src={profilePicture}
                className={styles.avatar}
                alt="user"
                onError={(e) => {
                  e.target.src = avatarImage;
                }}
              />
              <div className={styles.userInfo}>
                <span>Hello</span>
                <strong>{user?.display_name || user?.first_name}</strong>
              </div>
              <span className={styles.dropdownArrow}>▾</span>
            </div>

            {openMenu && (
              <div className={styles.dropdownMenu} ref={menuRef}>
                <p>
                  <strong>
                    {user?.first_name} {user?.last_name}
                  </strong>
                </p>
                <p className={styles.role}>{user?.roles[0]}</p>
                <p className={styles.email}>{user?.email}</p>
                <hr />
                <p onClick={() => setIsMyProfileScreen()}>My Profile</p>
                <p onClick={() => setIsMyMembershipScreen()}>My Subscription</p>
                <p
                  className={styles.signOut}
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                >
                  Sign Out
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Global search sheet */}
      {searchOpen && (
        <div
          className={styles.searchBackdrop}
          onClick={() => setSearchOpen(false)}
        >
          <div
            className={styles.searchSheet}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search input */}
            <div className={styles.searchInputRow}>
              <FiSearch className={styles.searchSheetIcon} />
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchSheetInput}
                placeholder="Search channels or communities…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className={styles.searchClearBtn}
                  onClick={() => setSearchQuery("")}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Results */}
            <div className={styles.searchResults}>
              {!searchQuery.trim() ? (
                <p className={styles.searchEmptyState}>
                  Type to search across all channels
                </p>
              ) : filteredChannels.length === 0 ? (
                <p className={styles.searchEmptyState}>No channels found</p>
              ) : (
                <ul className={styles.searchResultList}>
                  {filteredChannels.map((channel) => {
                    const community = communities.find(
                      (c) => c.name === channel.community
                    );
                    const iconUrl = community
                      ? ENDPOINTS.IMAGE_BASE_URL + community.icon
                      : null;
                    const isJoined = userJoinedChannels.find(
                      (e) => e.channel_id === channel.id
                    );
                    const unread = isJoined
                      ? unreadChatCounts[channel.id] || 0
                      : 0;
                    return (
                      <li
                        key={channel.id}
                        className={styles.searchResultRow}
                        onClick={() => handleChannelSelect(channel)}
                      >
                        <div className={styles.searchResultIcon}>
                          {iconUrl ? (
                            <img
                              src={iconUrl}
                              alt={channel.community}
                              className={styles.searchResultIconImg}
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <span
                            className={styles.searchResultIconInitial}
                            style={iconUrl ? { display: "none" } : {}}
                          >
                            {channel.name?.[0]?.toUpperCase() || "?"}
                          </span>
                        </div>
                        <div className={styles.searchResultInfo}>
                          <span className={styles.searchResultName}>
                            {channel.name}
                          </span>
                          <span className={styles.searchResultSub}>
                            {channel.community}
                          </span>
                        </div>
                        {unread > 0 && (
                          <div className={styles.searchResultBadge}>
                            <BadgeCount count={unread} />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AppBar;
